<script setup lang="ts">
import i18n from '@/plugins/i18n'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { Loader, InfoIconWithTooltip } from '@/components/app'
import { useOrganisationSettingsQuery, useUpdateOrganisationSettings } from '@/composable/queries/Dara/organisationSettings'

const authStore = useAuthStore()

const appStore = useAppStore()

type FormToUpdate = {
  [K in keyof App.Http.Resources.ApiV3.OrganisationSettingResource]?: boolean;
};

const selectedOrganisationId = computed(() => authStore.selected_organisation_id ?? 0)
const isQueryEnabled = computed(() => !!authStore.organisation?.id)

const {
  isPending,
  isError,
  error,
  data: settingsData,
} = useOrganisationSettingsQuery(selectedOrganisationId, isQueryEnabled)

const updateMutation = useUpdateOrganisationSettings(selectedOrganisationId.value)

const isSubjectsAndQualificationsCompleted = ref<boolean>(false)
const isYearGroupsAndSubgroupsCompleted = ref<boolean>(false)
const isStudentsAdded = ref<boolean>(false)
const isLicencesOrdered = ref<boolean>(false)
const isStaffSetup = ref<boolean>(false)

const onSubjectsAndQualificationsUpdate = () => {
  onSettingsUpdate({
    is_subjects_setup: isSubjectsAndQualificationsCompleted.value,
    is_qualifications_setup: isSubjectsAndQualificationsCompleted.value,
  })
}

const onYearGroupUpdate = () => {
  onSettingsUpdate({
    is_year_groups_setup: isYearGroupsAndSubgroupsCompleted.value,
    is_sub_year_groups_setup: isYearGroupsAndSubgroupsCompleted.value,
  })
}

const onTaskUpdate = (fieldToUpdate: keyof App.Http.Resources.ApiV3.OrganisationSettingResource, value: boolean) => {
  onSettingsUpdate({
    [fieldToUpdate]: value,
  })
}

const onSettingsUpdate = (dataToUpdate: FormToUpdate) => {
  const organisationSettingsId = settingsData && settingsData.value?.id

  if (organisationSettingsId) {
    updateMutation.mutate({ organisationSettingsId, formData: dataToUpdate },
      { onSuccess: () => {
        appStore.setSnackbar({
          colour: 'success',
          text: i18n.global.t('pages.dashboard.tasks_card_success_snackbar'),
        })
      } })
  }
}

const setSettings = () => {
  isSubjectsAndQualificationsCompleted.value = !!(settingsData.value?.is_subjects_setup && settingsData.value?.is_qualifications_setup)
  isYearGroupsAndSubgroupsCompleted.value = !!(settingsData.value?.is_year_groups_setup && settingsData.value?.is_sub_year_groups_setup)
  isStudentsAdded.value = !!(settingsData.value?.is_students_added)
  isLicencesOrdered.value = !!(settingsData.value?.is_ordered_licences)
  isStaffSetup.value = !!(settingsData.value?.is_myhq_users_setup)
}

onMounted(()=> {
  setSettings()
})

watch (settingsData, () => {
  setSettings()
})
</script>

<template>
  <VCard class="pa-4 elevation-5 flex-grow-1">
    <div
      v-if="isPending"
      class="d-flex"
    >
      <Loader />
    </div>
    <div v-else-if="isError">
      <VAlert type="error">
        {{ error }}
      </VAlert>
    </div>
    <div v-else-if="settingsData">
      <h4 class="mb-2">
        {{ $t('pages.dashboard.tasks_card_header') }}
      </h4>

      <VTable density="compact">
        <thead>
          <tr>
            <th class="bg-grey-100">
              <span class="checklist-card__label">{{ $t('pages.dashboard.tasks_card_stage_column') }}</span>
            </th>
            <th class="bg-grey-100 text-center">
              <span class="checklist-card__label">{{ $t('pages.dashboard.students_card_complete_column') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <RouterLink
                :to="{name: 'organisation.subjects-and-qualifications'}"
                class="checklist-card__label mr-1"
              >
                {{ $t('pages.dashboard.tasks_card_subjects_qualifications') }}
              </RouterLink>
              <InfoIconWithTooltip tooltip-key="pages.dashboard.tasks_card_subjects_qualifications_help" />
            </td>
            <td>
              <VSwitch
                v-model="isSubjectsAndQualificationsCompleted"
                class="d-flex justify-center checklist-card__switch"
                color="success"
                true-icon="mdi-check"
                density="compact"
                hide-details
                inset
                @update:model-value="onSubjectsAndQualificationsUpdate"
              />
            </td>
          </tr>
          <tr>
            <td>
              <RouterLink
                :to="{name: 'organisation.year-groups.list'}"
                class="checklist-card__label mr-1"
              >
                {{ $t('pages.dashboard.tasks_card_year_groups') }}
              </RouterLink>
              <InfoIconWithTooltip tooltip-key="pages.dashboard.tasks_card_year_groups_help" />
            </td>
            <td>
              <VSwitch
                v-model="isYearGroupsAndSubgroupsCompleted"
                class="d-flex justify-center checklist-card__switch"
                color="success"
                true-icon="mdi-check"
                density="compact"
                hide-details
                inset
                @update:model-value="onYearGroupUpdate"
              />
            </td>
          </tr>
          <tr>
            <td>
              <RouterLink
                :to="{name: 'students.add-options'}"
                class="checklist-card__label mr-1"
              >
                {{ $t('pages.dashboard.tasks_card_add_students') }}
              </RouterLink>
              <InfoIconWithTooltip tooltip-key="pages.dashboard.tasks_card_add_students_help" />
            </td>
            <td>
              <VSwitch
                v-model="isStudentsAdded"
                class="d-flex justify-center checklist-card__switch"
                color="success"
                true-icon="mdi-check"
                density="compact"
                hide-details
                inset
                @update:model-value="onTaskUpdate('is_students_added', isStudentsAdded)"
              />
            </td>
          </tr>
          <tr>
            <td>
              <RouterLink
                :to="{ name: 'students.overview', query: {show_instructions: 1} }"
                class="checklist-card__label mr-1"
              >
                {{ $t('pages.dashboard.tasks_card_buy_licences') }}
              </RouterLink>
              <InfoIconWithTooltip tooltip-key="pages.dashboard.tasks_card_buy_licences_help" />
            </td>
            <td>
              <VSwitch
                v-model="isLicencesOrdered"
                class="d-flex justify-center checklist-card__switch"
                color="success"
                true-icon="mdi-check"
                density="compact"
                hide-details
                inset
                @update:model-value="onTaskUpdate('is_ordered_licences', isLicencesOrdered)"
              />
            </td>
          </tr>
          <tr>
            <td>
              <RouterLink
                :to="{name: 'organisation.staff.list'}"
                class="checklist-card__label mr-1"
              >
                {{ $t('pages.dashboard.tasks_card_review_staff') }}
              </RouterLink>
              <InfoIconWithTooltip tooltip-key="pages.dashboard.tasks_card_review_staff_help" />
            </td>
            <td>
              <VSwitch
                v-model="isStaffSetup"
                class="d-flex justify-center checklist-card__switch"
                color="success"
                true-icon="mdi-check"
                density="compact"
                hide-details
                inset
                @update:model-value="onTaskUpdate('is_myhq_users_setup', isStaffSetup)"
              />
            </td>
          </tr>
        </tbody>
      </VTable>
    </div>
  </VCard>
</template>

<style lang="scss">
.checklist-card {
  &__switch {
    zoom: 0.8;
  }
  &__label {
    color: rgb(var(--v-theme-on-surface));
  }
}
</style>
